/**
 * What promoting an existing account to staff actually does to it.
 *
 * Promotion is the one action on the Link Generator that changes an account
 * somebody is already using, so the confirm step has to say what changes rather
 * than ask "are you sure?". The backend reports the side effects as warning
 * codes on each promotable row (`_classify_staff_promotion` in
 * apps/users/views.py); this turns them into the sentences a superuser reads.
 *
 * Kept out of the page so the wording — the only thing standing between a
 * mistyped email and a modified account — can be tested.
 */

/** One line per warning code, in the order they matter to the person deciding. */
export const WARNING_ORDER = ['school_change', 'embed_tester', 'merchant_specialist', 'already_staff', 'no_password'];

export const describeWarning = (code, row) => {
  const current = row?.current_school?.name || 'none';
  const requested = row?.requested_school?.name || 'the selected school';
  switch (code) {
    case 'school_change':
      return `Their school changes from ${current} to ${requested}.`;
    case 'embed_tester':
      return 'They lose embed tester access — the two roles cannot be held at once.';
    case 'merchant_specialist':
      return 'They lose Merchant Pay specialist access — the two roles cannot be held at once.';
    case 'already_staff':
      return 'They are already staff, so this only repoints which school they administer.';
    case 'no_password':
      return 'They sign in with Google or Apple, so there is no password to send them.';
    default:
      return '';
  }
};

/** The warning lines for a row, ordered and free of anything unrecognized. */
export const promotionWarnings = (row) =>
  WARNING_ORDER
    .filter((code) => (row?.warnings || []).includes(code))
    .map((code) => describeWarning(code, row))
    .filter(Boolean);

/**
 * The whole confirm-dialog text for one promotion.
 *
 * Always ends by stating that the password is untouched. That is the question a
 * superuser is most likely to have about changing someone's existing account,
 * and the answer is the same every time, so it is not left to be inferred from
 * the absence of a warning.
 */
export const promotionConfirmText = (row) => {
  const name = [row?.user?.first_name, row?.user?.last_name].filter(Boolean).join(' ')
    || row?.email
    || 'this account';
  const school = row?.requested_school?.name || 'the selected school';
  const lines = [
    `Give ${name} staff access to ${school}?`,
    '',
    ...promotionWarnings(row).map((line) => `• ${line}`),
    '• Their password is not changed.',
  ];
  return lines.join('\n');
};

/** The short label under the email field once a row comes back as promotable. */
export const promotionNotice = (row) => {
  if (!row) return '';
  if (!row.can_promote) {
    return row.blocked_message || 'This account already exists and cannot be promoted.';
  }
  return row.warnings?.includes('already_staff')
    ? 'Already is a staff account, you can update their school below.'
    : 'Already has an account, you can promote it to staff below.';
};
